import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Palette, TrendingUp, Target, Sparkles, BarChart3, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "../components/SEOHead";

const HomePage = () => {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({ experience: 0, projects: 0, growth: 0, satisfaction: 0 });

  useEffect(() => {
    // Animate stats
    const duration = 2000;
    const steps = 50;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        experience: Math.floor(15 * progress),
        projects: Math.floor(150 * progress),
        growth: Math.floor(3 * progress),
        satisfaction: Math.floor(100 * progress),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: t('services.visibility.title'),
      description: t('services.visibility.tagline'),
      features: language === 'fr' ? ["Site web", "SEO", "Social media"] : ["Website", "SEO", "Social media"],
      path: "/services/visibilite",
      color: "cyan"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: t('services.brandImage.title'),
      description: t('services.brandImage.tagline'),
      features: language === 'fr' ? ["Branding", "Supports commerciaux", "Contenus visuels"] : ["Branding", "Sales materials", "Visual content"],
      path: "/services/image-de-marque",
      color: "cyan"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: t('services.growth.title'),
      description: t('services.growth.tagline'),
      features: language === 'fr' ? ["Ads", "Automation", "Lead generation", "Tunnels"] : ["Ads", "Automation", "Lead generation", "Funnels"],
      path: "/services/croissance",
      color: "cyan"
    }
  ];

  const whyUs = [
    { icon: <Target className="w-6 h-6" />, title: t('home.strategicApproach'), desc: t('home.strategicApproachDesc') },
    { icon: <Sparkles className="w-6 h-6" />, title: t('home.creativeExcellence'), desc: t('home.creativeExcellenceDesc') },
    { icon: <BarChart3 className="w-6 h-6" />, title: t('home.measurableResults'), desc: t('home.measurableResultsDesc') },
  ];

  return (
    <div className="overflow-hidden">
      <SEOHead 
        title={language === 'fr' 
          ? "ImpaKt Studio | Agence Premium de Branding & Stratégie Digitale" 
          : "ImpaKt Studio | Premium Branding & Digital Strategy Agency"
        }
        description={language === 'fr'
          ? "Transformez votre entreprise en marque premium. Branding, stratégie digitale, visibilité web et croissance. 15+ ans d'expertise."
          : "Transform your business into a premium brand. Branding, digital strategy, web visibility and growth. 15+ years of expertise."
        }
        path="/"
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen gradient-hero flex items-center" data-testid="hero-section">
        {/* Watermark */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 text-[20rem] font-black text-white/5 select-none pointer-events-none hidden lg:block">
          IK
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-4xl">
            <p className="text-white/80 text-sm font-medium tracking-wider uppercase mb-6 animate-fade-in-up">
              {t('hero.subtitle')}
            </p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-playfair text-white leading-tight mb-8 animate-fade-in-up stagger-1">
              {t('hero.title1')}{" "}
              <span className="italic text-cyan-300">{t('hero.titleHighlight')}</span>
              <br />
              {t('hero.title2')}
            </h1>
            <p className="text-xl text-white/80 mb-10 max-w-2xl animate-fade-in-up stagger-2">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-cyan-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 hover:shadow-lg"
                data-testid="hero-cta-primary"
              >
                {t('hero.cta1')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-cyan-200 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                {t('hero.cta2')}
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-10 border-t border-white/20">
            <div className="text-center animate-count-up" data-testid="stat-experience">
              <div className="text-4xl md:text-5xl font-bold text-cyan-300">{stats.experience}+</div>
              <div className="text-white/70 text-sm mt-2">{t('stats.years')}</div>
            </div>
            <div className="text-center animate-count-up stagger-1" data-testid="stat-projects">
              <div className="text-4xl md:text-5xl font-bold text-cyan-300">{stats.projects}+</div>
              <div className="text-white/70 text-sm mt-2">{t('stats.projects')}</div>
            </div>
            <div className="text-center animate-count-up stagger-2" data-testid="stat-growth">
              <div className="text-4xl md:text-5xl font-bold text-cyan-300">{stats.growth}x</div>
              <div className="text-white/70 text-sm mt-2">{t('stats.growth')}</div>
            </div>
            <div className="text-center animate-count-up stagger-3" data-testid="stat-satisfaction">
              <div className="text-4xl md:text-5xl font-bold text-cyan-300">{stats.satisfaction}%</div>
              <div className="text-white/70 text-sm mt-2">{t('stats.satisfaction')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('home.expertiseTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
              {t('home.expertiseSubtitle')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('home.expertiseDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-cyan-100 flex items-center justify-center text-cyan-600 mb-6 group-hover:bg-cyan-500 group-hover:text-white transition-colors duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.features.map((feature) => (
                    <span key={feature} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                <Link
                  to={service.path}
                  className="inline-flex items-center text-cyan-600 font-semibold hover:gap-3 gap-2 transition-all"
                >
                  {t('common.learnMore')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-cyan-500 text-cyan-600 font-semibold rounded-full hover:bg-cyan-500 hover:text-white transition-all duration-300"
              data-testid="view-all-services"
            >
              {t('home.viewAllServices')}
            </Link>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 bg-gray-50" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('home.whyUsTitle')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                {t('home.whyUsSubtitle')}
              </h2>
              <p className="text-gray-600 mb-10 text-lg leading-relaxed">
                {t('home.whyUsDesc')}
              </p>
              
              <div className="space-y-6">
                {whyUs.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600 flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/a-propos"
                className="inline-flex items-center gap-2 mt-10 text-cyan-600 font-semibold hover:gap-3 transition-all"
                data-testid="discover-history"
              >
                {t('home.discoverHistory')} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt={language === 'fr' ? 'Équipe ImpaKt Studio' : 'ImpaKt Studio Team'}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">8</div>
                    <div className="text-gray-600 text-sm">{t('home.officesWorld')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 gradient-hero" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-white/80 text-lg mb-10">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-cyan-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg"
              data-testid="cta-contact"
            >
              {t('home.ctaButton1')}
            </Link>
            <Link
              to="/offres"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              data-testid="cta-offers"
            >
              {t('home.ctaButton2')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
